import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { FileCode, Zap, Layers, ShieldCheck, Cpu, Code } from "lucide-react";

// --- Helper Components ---

// StarRating Helper
const StarRating = ({ rating }: { rating: number }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <svg
        key={i}
        className={`h-4 w-4 ${
          i <= rating ? "text-yellow-500" : "text-gray-300" 
        } inline-block`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.783.57-1.838-.197-1.538-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.381-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
      </svg>
    );
  }
  return <div className="flex items-center">{stars}</div>;
};

// Verified Badge
const VerifiedBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 ml-2 border border-gray-300">
    <svg
      className="w-3 h-3 mr-1 text-yellow-600"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
    Verified
  </span>
);

// --- Tools Section (JS & TS Specific) ---
const ToolsYouWillMaster = () => {
  const tools = [
    { name: 'JavaScript', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/javascript/javascript-original.svg' },
    { name: 'TypeScript', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/typescript/typescript-original.svg' },
    { name: 'Node.js', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nodejs/nodejs-original.svg' },
    { name: 'NPM', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/npm/npm-original-wordmark.svg' },
    { name: 'VS Code', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/vscode/vscode-original.svg' },
    { name: 'Webpack', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/webpack/webpack-original.svg' },
    { name: 'Jest', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/jest/jest-plain.svg' },
    { name: 'ESLint', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/eslint/eslint-original.svg' },
  ];

  return (
    <section className="py-20 bg-gray-100">
      <div className="max-w-7xl mx-auto px-6 text-center">
        <h2 className="text-3xl md:text-4xl font-bold mb-12 text-black font-aligran">
          The Modern <span className="text-yellow-600">JS Stack</span>
        </h2>
        
        <div className="grid grid-cols-4 md:grid-cols-8 gap-8 mb-8">
          {tools.map((tool, i) => (
            <motion.div
              key={i}
              className="flex flex-col items-center justify-center p-4 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
            >
              <img
                src={tool.logo}
                alt={`${tool.name} Logo`}
                className="w-16 h-16 mb-2 object-contain"
              />
              <p className="text-sm font-semibold text-black mt-2">{tool.name}</p>
            </motion.div>
          ))}
        </div>
        
        <p className="text-gray-600 max-w-2xl mx-auto">
          We go beyond basics. Master <b>ES6+ Features</b>, <b>Asynchronous Programming</b>, and strict typing with <b>TypeScript</b>.
        </p>
      </div>
    </section>
  );
};

const JavaScriptTypeScript = () => {
  // --- Data ---
  const testimonials = [
    {
      id: 1,
      name: "Amit Patel",
      review:
        "I thought I knew JavaScript until I took this course. The deep dive into Closures, Prototypes, and 'this' keyword changed my perspective.",
      rating: 5,
      date: "2024-08-22",
      profileImg: "https://randomuser.me/api/portraits/men/11.jpg", 
      source: "Google Reviews",
    },
    {
      id: 2,
      name: "Sneha Reddy",
      review:
        "TypeScript is a must for React developers. This course taught me how to write type-safe code that doesn't break in production.",
      rating: 5,
      date: "2024-08-15",
      profileImg: "https://randomuser.me/api/portraits/women/23.jpg", 
      source: "LinkedIn",
    },
    {
      id: 3,
      name: "Rahul Verma",
      review:
        "The Asynchronous JavaScript module (Promises, Async/Await) was fantastic. I finally understand how the Event Loop works.",
      rating: 4,
      date: "2024-08-08",
      profileImg: "https://randomuser.me/api/portraits/men/41.jpg", 
      source: "Website Feedback",
    },
    {
      id: 4,
      name: "Priya Sharma",
      review:
        "From Vanilla JS to modern TypeScript tooling like Webpack and Babel. This course covers the entire ecosystem perfectly.",
      rating: 5,
      date: "2024-07-30",
      profileImg: "https://randomuser.me/api/portraits/women/65.jpg",
      source: "Google Reviews",
    },
    {
      id: 5,
      name: "Vikram Singh",
      review:
        "Great for interview prep. They cover tricky JS questions that are asked in almost every frontend interview.",
      rating: 4,
      date: "2024-07-25",
      profileImg: "https://randomuser.me/api/portraits/men/50.jpg",
      source: "LinkedIn",
    },
  ];

  const faqContent = [
    {
      title: "Why learn both JS and TS?",
      answer:
        "JavaScript is the language of the web. TypeScript is a superset that adds types, making large-scale applications safer and easier to maintain. Industry demands both.",
    },
    {
      title: "Is this course for absolute beginners?",
      answer:
        "Yes! We start with JavaScript from scratch (variables, loops). Then we gradually move to advanced concepts and finally TypeScript.",
    },
    {
      title: "Will we use frameworks like React or Angular?",
      answer:
        "No. This course focuses purely on the language fundamentals. Mastering JS/TS here will make learning any framework (React/Angular/Vue) incredibly easy later.",
    },
    {
      title: "What is ES6+?",
      answer:
        "ES6 (ECMAScript 2015) introduced major features like Arrow Functions, Classes, and Promises. We cover features up to the latest ES2024 version.",
    },
    {
      title: "What projects will I build?",
      answer:
        "You will build pure JS projects like a Weather App, Todo List with LocalStorage, an Interactive Quiz, and a TS-based Task Manager.",
    },
  ];

  const curriculumContent = [
    {
      title: "Module 1: JS Fundamentals & Logic",
      description: "Variables, Data Types, Type Coercion, Loops, Functions, and Scope (Global/Local/Block).",
    },
    {
      title: "Module 2: DOM Manipulation & Events",
      description: "Selecting elements, changing content dynamically, Event Listeners, and Event Bubbling/Capturing.",
    },
    {
      title: "Module 3: Advanced JavaScript",
      description: "Deep dive into Closures, Hoisting, the 'this' keyword, Call/Apply/Bind, and Prototypal Inheritance.",
    },
    {
      title: "Module 4: Asynchronous JavaScript",
      description: "Callbacks, Promises, Async/Await, Fetch API, and understanding the Event Loop & Callback Queue.",
    },
    {
      title: "Module 5: Modern JS (ES6+ Features)",
      description: "Arrow Functions, Destructuring, Spread/Rest operators, Modules (Import/Export), and Map/Set.",
    },
    {
      title: "Module 6: TypeScript Basics",
      description: "Why TS?, Static Typing, Interfaces, Types, Enums, and compiling TS to JS.",
    },
    {
      title: "Module 7: Advanced TypeScript",
      description: "Generics, Union/Intersection Types, Type Guards, Decorators, and Utility Types.",
    },
    {
      title: "Module 8: Tooling & Testing",
      description: "Setting up Webpack, Babel, ESLint, and writing Unit Tests using Jest.",
    },
  ];

  // Highlights Data with JS/TS Specific Images
  const highlights = [
    { 
      title: 'Deep Foundations', 
      desc: 'Master Closures & Prototypes.', 
      img: 'https://images.unsplash.com/photo-1579468118864-1b9ea3c0db4a?q=80&w=2070' // Code/Structure
    },
    { 
      title: 'Modern ES6+', 
      desc: 'Arrow functions & Async/Await.', 
      img: 'https://images.unsplash.com/photo-1627398242454-45a1465c2479?q=80&w=2074' // Modern Code
    },
    { 
      title: 'TypeScript Power', 
      desc: 'Write Type-Safe robust code.', 
      img: 'https://images.unsplash.com/photo-1516116216624-53e697fedbea?q=80&w=2128' // Security/Types
    },
    { 
      title: 'DOM Magic', 
      desc: 'Build interactive UI without frameworks.', 
      img: 'https://images.unsplash.com/photo-1507721999472-8ed4421c4af2?q=80&w=2070' // Interactive UI
    },
    { 
      title: 'Async Mastery', 
      desc: 'Understand Event Loop & Promises.', 
      img: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=2070' // Timing/Clock/Loop
    },
    { 
      title: 'Mini Projects', 
      desc: 'Weather App, Quiz & Games.', 
      img: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?q=80&w=1974' // Gaming/App
    },
    { 
      title: 'Expert Mentors', 
      desc: 'Learn from Senior Frontend Devs.', 
      img: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=2070' // Team
    },
    { 
      title: 'Certification', 
      desc: 'JS & TS Specialist Certificate.', 
      img: 'https://images.unsplash.com/photo-1589330273594-fade1ee91647?q=80&w=2070' // Certificate
    },
    { 
      title: 'Interview Prep', 
      desc: 'Crack JS Output-based questions.', 
      img: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=2069' // Interview
    },
  ];

  // --- Logic for Carousel ---
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(3);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setVisibleCount(3);
      else if (window.innerWidth >= 768) setVisibleCount(2);
      else setVisibleCount(1);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const autoScrollInterval = setInterval(() => {
      setCurrentTestimonialIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(autoScrollInterval);
  }, [testimonials.length]);

  const currentVisibleTestimonials = [];
  for (let i = 0; i < visibleCount; i++) {
    currentVisibleTestimonials.push(
      testimonials[(currentTestimonialIndex + i) % testimonials.length]
    );
  }

  // --- Logic for Form ---
  const [formData, setFormData] = useState({ firstName: "", businessEmail: "", phone: "" });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert("Thank you! We will contact you shortly regarding the JS/TS course.");
    setFormData({ firstName: "", businessEmail: "", phone: "" });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="bg-white text-gray-900 font-sans">
      <Helmet>
        <title>JavaScript & TypeScript Course | From Zero to Hero</title>
        <meta name="description" content="Master Core JavaScript (ES6+) and Advanced TypeScript. Build a strong foundation for Web Development." />
      </Helmet>

      {/* --- HERO SECTION --- */}
      <section className="relative overflow-hidden h-[550px] flex items-center bg-black"> 
      
      {/* Background Image + Gradient */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1627398242454-45a1465c2479?q=80&w=2074&auto=format&fit=crop"
          alt="JavaScript Code"
          className="w-full h-full object-cover opacity-30"
        />
        {/* Yellow/Blue Gradient for JS/TS Theme */}
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-yellow-900/30" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-center w-full pt-20">

        {/* Left Content */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-block px-3 py-1 mb-4 border border-yellow-500 rounded-full bg-yellow-900/30 text-yellow-400 text-sm font-semibold tracking-wide">
            WEB FUNDAMENTALS
          </div>

          <h1 className="text-4xl md:text-5xl font-extrabold leading-tight mb-6 text-gray-100 font-aligran">
            Master JavaScript
            <span className="text-yellow-500 block">& TypeScript</span>
          </h1>

          <p className="text-xl text-gray-300 max-w-2xl mb-8">
            The language that powers the web. Learn modern <strong>ES6+</strong>, asynchronous programming, and <strong>Type Safety</strong> with TypeScript.
          </p>

          <button
            onClick={() =>
              document.getElementById("enquiry-form")?.scrollIntoView({ behavior: "smooth" })
            }
            className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-4 px-10 rounded-lg shadow-[0_5px_20px_rgba(202,138,4,0.3)] transition duration-300 transform hover:scale-105"
          >
            Start Coding
          </button>
        </motion.div>

        {/* Right Side Image (JS Logo) */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9 }}
          className="flex justify-center md:justify-end"
        >
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/6/6a/JavaScript-logo.png" 
            alt="JavaScript Logo"
            className="w-64 md:w-72 
                       drop-shadow-[0_5px_30px_rgba(234,179,8,0.4)]
                       transition-transform duration-300 
                       hover:scale-105"
          />
        </motion.div>
      </div>
      </section>

      {/* --- WHY LEARN SECTION --- */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6 text-center">

          <h2 className="text-4xl md:text-5xl font-extrabold mb-4 text-gray-900 font-aligran">
            Why Learn <span className="text-yellow-600">JS & TS?</span>
          </h2>

          <p className="text-gray-600 mb-14 max-w-3xl mx-auto text-lg leading-relaxed">
            JavaScript is ubiquitous. It runs on browsers, servers (Node.js), and even robots. TypeScript adds the safety needed for enterprise apps.
          </p>

          <div className="grid md:grid-cols-3 gap-8">

            <div className="bg-white border border-gray-200 p-8 rounded-2xl shadow-md hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group">
              <div className="w-14 h-14 bg-yellow-100 text-yellow-600 rounded-full flex items-center justify-center mb-5 mx-auto group-hover:scale-110 transition">
                <FileCode size={28} />
              </div>
              <h3 className="font-bold text-2xl text-black mb-3">Web Foundation</h3>
              <p className="text-gray-600 leading-relaxed">
                You cannot become a Web Developer without JavaScript. It is the only language that runs natively in the browser.
              </p>
            </div>

            <div className="bg-white border border-gray-200 p-8 rounded-2xl shadow-md hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group">
              <div className="w-14 h-14 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mb-5 mx-auto group-hover:scale-110 transition">
                <ShieldCheck size={28} />
              </div>
              <h3 className="font-bold text-2xl text-black mb-3">TypeScript Safety</h3>
              <p className="text-gray-600 leading-relaxed">
                Catch errors before you run the code. TypeScript is the industry standard for large React/Angular projects.
              </p>
            </div>

            <div className="bg-white border border-gray-200 p-8 rounded-2xl shadow-md hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group">
              <div className="w-14 h-14 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-5 mx-auto group-hover:scale-110 transition">
                <Zap size={28} />
              </div>
              <h3 className="font-bold text-2xl text-black mb-3">Versatility</h3>
              <p className="text-gray-600 leading-relaxed">
                Learn once, build anywhere: Frontend, Backend (Node.js), Mobile (React Native), and Desktop (Electron).
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* --- HIGHLIGHTS SECTION --- */}
      <section className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-gray-900 font-aligran">
            Course <span className="text-yellow-600">Highlights</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {highlights.map((item, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-xl shadow-md hover:shadow-lg transition overflow-hidden flex h-44 border-l-4 border-yellow-600"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <div className="w-1/2 p-5 flex flex-col justify-center">
                  <h3 className="font-bold text-lg text-black mb-1">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
                <div className="w-1/2 relative">
                  <img src={item.img} alt={item.title} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-l from-black/40 to-transparent"></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* --- CURRICULUM SECTION --- */}
      <section className="py-20 bg-white"> 
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-left text-gray-900 font-aligran"> 
            Detailed <span className="text-yellow-600">Curriculum</span>
          </h2>
          <div className="space-y-4">
            {curriculumContent.map((module, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50"> 
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-100 hover:bg-gray-200 transition flex justify-between items-center text-gray-900"> 
                    {module.title}
                    <span className="transition-transform group-open:rotate-180 text-gray-900">▼</span>
                  </summary>
                  <div className="px-6 py-4 text-black border-t border-gray-200 leading-relaxed"> 
                    <p>{module.description}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- TOOLS SECTION --- */}
      <ToolsYouWillMaster />

      {/* --- FAQ & FORM SECTION --- */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-3 gap-10 bg-white"> 
        <div className="md:col-span-2">
          <h2 className="text-3xl font-bold mb-8 text-gray-900 font-aligran">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqContent.map((item, idx) => (
              <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg"> 
                <details className="group p-4">
                  <summary className="cursor-pointer font-medium text-gray-900 hover:text-gray-700 flex justify-between">
                    {item.title}
                    <span className="text-gray-900 group-open:text-yellow-600">+</span>
                  </summary>
                  <p className="mt-4 text-black text-sm border-t border-gray-200 pt-3">{item.answer}</p> 
                </details>
              </div>
            ))}
          </div>
        </div>

        <div className="md:col-span-1" id="enquiry-form">
          <div className="bg-gray-100 border border-yellow-300 rounded-xl p-6 shadow-xl sticky top-10">
            <h3 className="text-2xl font-bold mb-2 text-center text-yellow-600">Book Your Seat</h3>
            <p className="text-center text-gray-600 mb-6 text-sm">Become a JS Expert.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input type="text" name="firstName" placeholder="Your Name" required className="w-full bg-white border border-gray-300 rounded p-3 text-black focus:border-yellow-500 focus:outline-none" value={formData.firstName} onChange={handleInputChange} />
              <input type="email" name="businessEmail" placeholder="Email Address" required className="w-full bg-white border border-gray-300 rounded p-3 text-black focus:border-yellow-500 focus:outline-none" value={formData.businessEmail} onChange={handleInputChange} />
              <input type="tel" name="phone" placeholder="Phone Number" required className="w-full bg-white border border-gray-300 rounded p-3 text-black focus:border-yellow-500 focus:outline-none" value={formData.phone} onChange={handleInputChange} />
              <button type="submit" className="w-full bg-gradient-to-r from-yellow-600 to-yellow-700 hover:from-yellow-700 hover:to-yellow-800 text-white font-bold py-3 rounded transition shadow-lg">ENQUIRE NOW</button>
              <p className="text-xs text-center text-gray-500 mt-2">We respect your privacy.</p>
            </form>
          </div>
        </div>
      </section>

      {/* --- TESTIMONIALS SECTION --- */}
      <section className="bg-gray-50 py-20 border-t border-gray-200"> 
        <div className="max-w-7xl mx-auto px-6 text-left"> 
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-gray-900 font-aligran">
                Success <span className="text-yellow-600">Stories</span>
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
                <AnimatePresence mode="popLayout">
                    {currentVisibleTestimonials.map((testimonial) => (
                        <motion.div
                            key={testimonial.id}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.4 }}
                            className="bg-white p-6 rounded-xl border border-gray-200 flex flex-col text-left hover:border-yellow-400/50 transition shadow-sm"
                        >
                            <div className="flex items-center mb-4">
                                <img src={testimonial.profileImg} alt={testimonial.name} className="w-12 h-12 rounded-full border-2 border-yellow-500 mr-3" />
                                <div>
                                    <h4 className="font-bold text-black">{testimonial.name}</h4> 
                                    <span className="text-gray-500 text-xs">{testimonial.source}</span>
                                </div>
                            </div>
                            <div className="mb-3"><StarRating rating={testimonial.rating} /></div>
                            <p className="text-gray-700 text-sm flex-grow">"{testimonial.review}"</p> 
                            <VerifiedBadge />
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>
            <div className="flex justify-start mt-8 space-x-2"> 
                {testimonials.map((_, idx) => (
                    <button
                        key={idx}
                        onClick={() => setCurrentTestimonialIndex(idx)}
                        className={`h-2 w-2 rounded-full transition-all ${idx === currentTestimonialIndex ? 'bg-yellow-600 w-6' : 'bg-gray-300'}`}
                    />
                ))}
            </div>
        </div>
      </section>
    </div>
  );
};

export default JavaScriptTypeScript;